import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs, onSnapshot, query } from "firebase/firestore";
import { db } from "../lib/firebase";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar";

import {
  Users, UserCog, ClipboardList, Wrench, RefreshCw, 
  HeartPulse, Zap, Activity, Clock, ShieldCheck, AlertCircle
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [functionsList, setFunctionsList] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [uSnap, oSnap, fSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "orders")),
        getDocs(collection(db, "functions")),
      ]);
      setUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setOrders(oSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setFunctionsList(fSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    
    const qOrders = query(collection(db, "orders"));
    const unsubOrders = onSnapshot(qOrders, (snap) => {
        setOrders(snap.docs.map(d => ({id: d.id, ...d.data()})));
    });

    const qUsers = query(collection(db, "users"));
    const unsubUsers = onSnapshot(qUsers, (snap) => {
        setUsers(snap.docs.map(d => ({id: d.id, ...d.data()})));
    });

    return () => {
        unsubOrders();
        unsubUsers();
    };
  }, []);

  const stats = useMemo(() => {
    // 1. Clientes: Todos que não são administradores (técnicos inclusos)
    const allClients = users.filter(u => !u?.isAdmin);
    
    // 2. Técnicos: Apenas quem tem a flag de técnico
    const technicians = users.filter(u => u?.userType?.toLowerCase() === "technician" || u?.isTechnician === true);
    
    // 3. Pendentes de Aprovação (Usa technicianStatus ou similar)
    const techsPending = technicians.filter(t => t?.technicianStatus === "pending" || t?.status === "pending_approval");
    
    const functionsActive = functionsList.filter(f => f?.active !== false && f?.status !== "inativo");

    return {
      clients: allClients.length,
      totalTechs: technicians.length,
      techsActive: technicians.length - techsPending.length,
      techsPending: techsPending.length,
      totalOrders: orders.length,
      activeOrders: orders.filter(o => o.status === 'open' || o.status === 'in_progress').length,
      totalFunctions: functionsList.length,
      functionsActive: functionsActive.length,
      functionsInactive: functionsList.length - functionsActive.length
    };
  }, [users, orders, functionsList]);

  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400 uppercase">Sincronizando...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">Painel <span className="text-blue-600">Administrativo</span></h1>
              <p className="text-[10px] font-black text-slate-400 mt-2 flex items-center gap-2 uppercase tracking-widest leading-none">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> Monitoramento em tempo real
              </p>
            </div>
            <Button onClick={loadData} variant="outline" className="h-12 px-6 rounded-2xl font-black uppercase italic text-[10px] tracking-widest gap-2 bg-white border-slate-200 shadow-xl active:scale-95 transition-all">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar Dados
            </Button>
          </header>

          {/* CARDS SUPERIORES INTERATIVOS - AGORA COM 5 COLUNAS NO XL */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-10">
            <button onClick={() => navigate('/admin-clients')} className="text-left outline-none group active:scale-95 transition-all">
              <Card className="rounded-[2.5rem] border-none shadow-xl bg-white group-hover:ring-2 ring-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-blue-50 rounded-2xl text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all"><Users size={20} /></div>
                    <Badge className="bg-blue-50 text-blue-600 border-none font-black text-[9px] uppercase italic px-3 tracking-widest">Geral</Badge>
                  </div>
                  <h3 className="text-3xl font-black text-slate-900 tracking-tighter leading-none">{loading ? "..." : stats.clients}</h3>
                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-2 italic">Usuários Totais</p>
                </CardContent>
              </Card>
            </button>

            <button onClick={() => navigate('/admin-tecs')} className="text-left outline-none group active:scale-95 transition-all">
              <Card className="rounded-[2.5rem] border-none shadow-xl bg-white group-hover:ring-2 ring-orange-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-orange-50 rounded-2xl text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-all"><UserCog size={20} /></div>
                    <Badge className="bg-orange-50 text-orange-600 border-none font-black text-[9px] uppercase italic px-3 tracking-widest">Técnicos</Badge>
                  </div>
                  <h3 className="text-3xl font-black text-slate-900 tracking-tighter leading-none">{loading ? "..." : stats.totalTechs}</h3>
                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-2 italic">Rede Profissional</p>
                </CardContent>
              </Card>
            </button>

            {/* NOVO CARD: APROVAÇÕES PENDENTES */}
            <button onClick={() => navigate('/admin-act-tecs')} className="text-left outline-none group active:scale-95 transition-all">
              <Card className={`rounded-[2.5rem] border-none shadow-xl bg-white group-hover:ring-2 ring-red-500 ${stats.techsPending > 0 ? 'border-b-4 border-red-500' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-2xl transition-all ${stats.techsPending > 0 ? 'bg-red-50 text-red-600 animate-pulse' : 'bg-slate-50 text-slate-400'}`}>
                        <ShieldCheck size={20} />
                    </div>
                    <Badge className="bg-red-50 text-red-600 border-none font-black text-[9px] uppercase italic px-3 tracking-widest">Pendentes</Badge>
                  </div>
                  <h3 className={`text-3xl font-black tracking-tighter leading-none ${stats.techsPending > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                    {loading ? "..." : stats.techsPending}
                  </h3>
                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-2 italic">Aguardando Auditoria</p>
                </CardContent>
              </Card>
            </button>

            <button onClick={() => navigate('/admin-orders')} className="text-left outline-none group active:scale-95 transition-all">
              <Card className="rounded-[2.5rem] border-none shadow-xl bg-white group-hover:ring-2 ring-green-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-green-50 rounded-2xl text-green-600 group-hover:bg-green-600 group-hover:text-white transition-all"><ClipboardList size={20} /></div>
                    <Badge className="bg-green-50 text-green-600 border-none font-black text-[9px] uppercase italic px-3 tracking-widest">Serviços</Badge>
                  </div>
                  <h3 className="text-3xl font-black text-slate-900 tracking-tighter leading-none">{loading ? "..." : stats.totalOrders}</h3>
                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-2 italic">Ordens Totais</p>
                </CardContent>
              </Card>
            </button>

            <button onClick={() => navigate('/admin-functions')} className="text-left outline-none group active:scale-95 transition-all">
              <Card className="rounded-[2.5rem] border-none shadow-xl bg-white group-hover:ring-2 ring-purple-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-purple-50 rounded-2xl text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-all"><Wrench size={20} /></div>
                    <Badge className="bg-purple-50 text-purple-600 border-none font-black text-[9px] uppercase italic px-3 tracking-widest">Especialidades</Badge>
                  </div>
                  <h3 className="text-3xl font-black text-slate-900 tracking-tighter leading-none">{loading ? "..." : stats.totalFunctions}</h3>
                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-2 italic">Funções no App</p>
                </CardContent>
              </Card>
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 text-left">
            {/* AÇÕES RÁPIDAS (LADO ESQUERDO) */}
            <Card className="rounded-[3rem] border-none shadow-xl bg-white overflow-hidden">
              <div className="p-8 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
                <h3 className="text-xl font-black text-slate-800 uppercase italic tracking-tighter">Ações Rápidas</h3>
                <Activity size={20} className="text-slate-300" />
              </div>
              <CardContent className="p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { path: "/admin-clients", icon: Users, label: "Clientes", color: "text-blue-500" },
                  { path: "/admin-tecs", icon: UserCog, label: "Técnicos", color: "text-orange-500" },
                  { path: "/admin-act-tecs", icon: ShieldCheck, label: "Aprovar Técnicos", color: "text-red-500" },
                  { path: "/admin-orders", icon: ClipboardList, label: "Central de Ordens", color: "text-green-500" },
                  { path: "/admin-functions", icon: Wrench, label: "Especialidades", color: "text-purple-500" },
                  { path: "/admin-suspects", icon: Zap, label: "Antifraude", color: "text-yellow-600" },
                ].map(item => (
                  <Button 
                    key={item.path} 
                    onClick={() => navigate(item.path)} 
                    variant="ghost" 
                    className="justify-start h-16 rounded-2xl font-black uppercase italic text-[10px] tracking-widest gap-4 bg-slate-50 hover:bg-slate-900 hover:text-white transition-all group"
                  >
                    <div className={`p-2 rounded-xl bg-white shadow-sm group-hover:bg-slate-800 transition-colors ${item.color}`}>
                        <item.icon size={18} />
                    </div>
                    {item.label}
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* SAÚDE DO SISTEMA (LADO DIREITO) */}
            <Card className="rounded-[3rem] border-none shadow-2xl bg-slate-900 text-white flex flex-col">
              <div className="p-8 border-b border-white/5 flex items-center justify-between">
                <h3 className="text-xl font-black uppercase italic tracking-tighter flex items-center gap-3">
                  Saúde do <span className="text-blue-400">Sistema</span>
                  <HeartPulse className="text-red-500 animate-pulse" size={24} />
                </h3>
                <Clock size={20} className="text-slate-500" />
              </div>
              
              <CardContent className="p-8 flex-1 flex flex-col justify-between">
                <div className="space-y-4">
                    {[
                        { label: "Total de Usuários", value: stats.clients, color: "text-blue-400" },
                        { label: "Técnicos Operacionais", value: stats.techsActive, color: "text-green-400" },
                        { label: "Pendentes de Aprovação", value: stats.techsPending, color: "text-red-400" }, // Adicionado aqui
                        { label: "Ordens de Serviço", value: stats.totalOrders, color: "text-white" },
                        { label: "OS em Andamento", value: stats.activeOrders, color: "text-yellow-400" },
                    ].map(item => (
                        <div key={item.label} className="flex justify-between items-center border-b border-white/5 pb-3">
                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{item.label}</span>
                            <span className={`text-lg font-black ${item.color}`}>{loading ? "..." : item.value}</span>
                        </div>
                    ))}
                </div>

                <div className="mt-8 pt-6 border-t border-white/5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                             <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                             <span className="text-[9px] font-black uppercase text-slate-400">Servidor Operacional</span>
                        </div>
                        <span className="text-[9px] font-black uppercase text-green-500">100% Online</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="w-full h-full bg-blue-500 animate-pulse"></div>
                    </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </main>
      </div>
    </div>
  );
}